import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Attributes } from '~/components/ui/Attributes';
import { Confirm } from '~/components/ui/Confirm';
import { SearchField } from '~/components/ui/SearchField';
import { FragmentType, getFragmentData, graphql } from '~/gql';
import { useSearch } from '~/hooks/useSearch';
import { useBrandName } from '~/brand/BrandContext';
import { AttributeEditForm, AttributeEntityType } from './AttributeEditForm';

export const AttributeFieldsFragment = graphql(`
  fragment AttributeFields on Attribute {
    id
    type
    category
    name
    value
    attachments {
      id
      kind
      ext
      thumb
      url
      originalFilename
    }
  }
`);

type Attribute = NonNullable<FragmentType<typeof AttributeFieldsFragment>>;

export type HasAttributes = {
  attributes?: Attribute[] | null;
};

type Props = {
  attributes?: Attribute[] | null;
  integrationAttributes?: Attribute[] | null;
  integrationName?: string;
  isSearchSticked?: boolean;
  onUpdate: () => void;
  entityId: string;
  entityTypeName: string;
  hideEdit?: boolean;
  showIntegrationView?: boolean;
  isPopup?: boolean;
  /**
   * Optional prop to customize the appearance based on the parent form context.
   * When set to 'asset', removes the gray background from the search input area
   * and hides the back arrow button in editing mode, providing a cleaner
   * appearance when used within asset tabs where navigation is handled by the parent.
   */
  formParent?: string;
  isEditing?: boolean;
  onEditingChange?: (editing: boolean) => void;
};

const SEARCH_OPTIONS = {
  keys: ['name', 'value', 'category'],
};

export function AttributesList({
  attributes,
  integrationAttributes,
  integrationName,
  isSearchSticked = false,
  onUpdate,
  entityId,
  entityTypeName,
  hideEdit,
  showIntegrationView = false,
  formParent,
  isEditing: externalIsEditing,
  onEditingChange,
  isPopup,
}: Props) {
  const [view, setView] = useState(0);
  const brandName = useBrandName();

  return (
    <>
      {showIntegrationView &&
        integrationAttributes &&
        integrationAttributes.length > 0 && (
          <div className='flex px-4'>
            <div
              className={classNames(
                'flex h-12 flex-1 cursor-pointer flex-col items-center justify-center text-center text-sm font-normal not-italic leading-[normal] text-[#007070]',
                view === 0 && 'border-b-[3px] border-brand font-semibold'
              )}
              onClick={() => setView(0)}
            >
              {brandName}
            </div>
            <div
              className={classNames(
                'flex h-12 flex-1 cursor-pointer flex-col items-center justify-center text-center text-sm font-normal not-italic leading-[normal] text-[#007070]',
                view === 1 && 'border-b-[3px] border-brand font-semibold'
              )}
              onClick={() => setView(1)}
            >
              <span className='capitalize'>
                {integrationName ?? 'Integration'}
              </span>
            </div>
          </div>
        )}
      {view === 0 && (
        <AttributeDisplayList
          attributes={attributes ?? []}
          isSearchSticked={isSearchSticked}
          onUpdate={onUpdate}
          entityId={entityId}
          entityTypeName={entityTypeName}
          hideEdit={hideEdit || isPopup}
          formParent={formParent}
          isEditing={externalIsEditing}
          onEditingChange={onEditingChange}
        />
      )}
      {showIntegrationView && view === 1 && (
        <AttributeDisplayList
          attributes={integrationAttributes ?? []}
          isSearchSticked={isSearchSticked}
          onUpdate={onUpdate}
          entityId={entityId}
          entityTypeName={entityTypeName}
          hideEdit={true}
          formParent={formParent}
          isEditing={externalIsEditing}
          onEditingChange={onEditingChange}
        />
      )}
    </>
  );
}

function AttributeDisplayList({
  isSearchSticked = false,
  hideEdit,
  isEditing: externalIsEditing,
  onEditingChange,
  formParent,
  ...props
}: {
  attributes: Array<FragmentType<typeof AttributeFieldsFragment>>;
  onUpdate: () => void;
  isSearchSticked?: boolean;
  entityId: string;
  entityTypeName: string;
  hideEdit?: boolean;
  isEditing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /**
   * Optional prop to customize the appearance based on the parent form context.
   * When set to 'asset', removes the gray background from the search input area
   * and hides the back arrow button in editing mode.
   */
  formParent?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const attributes = getFragmentData(AttributeFieldsFragment, props.attributes);

  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Use external editing state if provided, otherwise use internal state
  const isEditing =
    externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;

  const setIsEditing = (editing: boolean) => {
    if (onEditingChange) {
      onEditingChange(editing);
    } else {
      setInternalIsEditing(editing);
    }
  };

  // CRITICAL FIX: Only use search processing when there's an actual search query
  // This maintains consistent ordering like AttributeEditForm when no search is active
  const searchableAttributes = useMemo(() => [...attributes], [attributes]);
  const { results: searchResults, search } = useSearch(
    searchableAttributes,
    SEARCH_OPTIONS
  );

  // Use original attributes when no search query, search results when searching
  // Convert readonly array to mutable array to satisfy Attributes component type requirements
  const displayAttributes = searchQuery.trim()
    ? searchResults
    : [...attributes];

  // CRITICAL FIX: Use useCallback to prevent search function from changing and add proper dependencies
  const clearSearchAndEditing = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = '';
      setSearchQuery('');
      search('');
      setIsEditing(false);
    }
  }, [search, setIsEditing]);

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      search(query);
    },
    [search]
  );

  useEffect(() => {
    // Only clear search and editing when entityId changes, not on every render
    return () => {
      if (inputRef.current) {
        inputRef.current.value = '';
        setSearchQuery('');
        search('');
        setIsEditing(false);
      }
    };
  }, [props.entityId]); // Remove clearSearchAndEditing from dependencies to prevent infinite loop

  const showEditButton = !isEditing && !hideEdit;

  // When formParent is 'asset', we remove the gray background and hide the back arrow
  // This provides a cleaner appearance when the AttributesList is used within asset tabs
  const isAssetFormParent = formParent === 'asset';

  return (
    <>
      <div className='flex flex-col'>
        <div
          id='search-attributes'
          className={classNames(
            'sticky top-0 z-10 flex items-center p-3',
            // Conditionally apply gray background - removed when formParent is 'asset'
            !isAssetFormParent && 'bg-[#f8f9fa]',
            isSearchSticked && 'shadow'
          )}
        >
          {/* Hide the back arrow when formParent is 'asset' since navigation is handled by the parent */}
          {isEditing && !isAssetFormParent && (
            <ArrowLeftIcon
              className='mr-2 h-5 cursor-pointer text-secondary'
              onClick={() => {
                if (isFormDirty) {
                  setIsConfirming(true);
                } else {
                  setIsEditing(false);
                }
              }}
            />
          )}

          <SearchField
            ref={inputRef}
            placeholder='Search Attributes...'
            onChange={(event) => handleSearch(event.target.value)}
          />

          {showEditButton && (
            <div
              className='ml-1 flex cursor-pointer items-center justify-center rounded-full hover:bg-grey-10'
              onClick={() => setIsEditing(true)}
            >
              <PencilIcon className='m-2 h-5 text-secondary' />
            </div>
          )}
        </div>
        {!attributes.length && !isEditing && (
          <p className='p-4 text-center'>
            There are no attributes for this {props.entityTypeName}
          </p>
        )}
      </div>

      {isEditing ? (
        <AttributeEditForm
          entityId={props.entityId}
          data={{ attributes: props.attributes }}
          search={inputRef.current?.value}
          onSuccess={() => {
            setIsEditing(false);
            setIsFormDirty(false);
          }}
          onFormTouched={setIsFormDirty}
        />
      ) : (
        <div className='m-4 mt-0'>
          <Attributes data={{ attributes: displayAttributes }} />
        </div>
      )}
      {isConfirming && (
        <Confirm
          onCancel={() => setIsConfirming(false)}
          onConfirm={() => {
            setIsEditing(false);
            setIsConfirming(false);
            setIsFormDirty(false);
          }}
          title='Discard unsaved changes?'
          body="If you proceed you will lose all the changes you've made."
          cancel='Go back'
          confirm='Lose changes'
        />
      )}
    </>
  );
}

export function getEntityType(id: string): AttributeEntityType {
  if (id.startsWith('Asset:')) return 'Asset';
  if (id.startsWith('Contact:')) return 'Contact';
  if (id.startsWith('Item:')) return 'Item';
  if (id.startsWith('Site:')) return 'Site';

  throw new Error('Invalid entity type');
}
